import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BookOpen,
  Layers,
  PencilLine,
  Plus,
  RotateCcw,
  Search,
  Trash2,
  X,
} from 'lucide-react';

import { PageHeader } from '@/components/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { QueryError } from '@/components/ui/QueryError';
import { Table, type Column } from '@/components/ui/Table';
import { Tabs, type TabOption } from '@/components/ui/Tabs';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { capitalizeWords } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { Subject, SubjectCategory } from '@/types/subject';
import { subjectsApi } from '../api/subjects.api';
import { ClassSubjectsView } from '../components/ClassSubjectsView';
import { SubjectFormModal } from '../components/SubjectFormModal';
import {
  SUBJECT_CATEGORY_LABEL,
  SUBJECT_CATEGORY_ORDER,
  SUBJECT_CATEGORY_PLURAL,
  SUBJECT_CATEGORY_TONE,
  SUBJECTS_BY_CLASS_VIEW,
  SUBJECTS_QUERY,
} from '../constants';

type View = 'ALL' | SubjectCategory | 'SUSPENDED';
/** The school's whole list, or one class's subjects at a time. */
type Mode = 'LIST' | 'BY_CLASS';

/**
 * The screen's filters, as they sit in the address bar. The mode keeps the
 * parameter class setup already links with (`?view=by-class`); the category
 * tab is `category`, since `view` was taken first.
 */
type Filters = {
  [SUBJECTS_QUERY.view]: typeof SUBJECTS_BY_CLASS_VIEW | '';
  category: View;
  search: string;
};

const DEFAULT_FILTERS: Filters = {
  [SUBJECTS_QUERY.view]: '',
  category: 'ALL',
  search: '',
};

/** What a link may set — the tabs this page draws, and the one other mode. */
const FILTER_OPTIONS = {
  allowed: {
    [SUBJECTS_QUERY.view]: [SUBJECTS_BY_CLASS_VIEW],
    category: ['ALL', ...SUBJECT_CATEGORY_ORDER, 'SUSPENDED'],
  },
};

/** How many class names a row shows before "and N more". */
const CLASSES_SHOWN = 3;

/**
 * The school's subject list — the one place a subject is added, renamed,
 * moved to another category or deleted. Deleting takes it off every class that
 * teaches it this year as well, so the confirmation names those classes.
 *
 * A class's three lists (core subjects, electives, languages) pick from here,
 * each from its own category, so a subject can never be core in one class and
 * an elective in another, and "Maths" can never sit beside "Mathematics": the
 * server refuses the second spelling and names the first.
 *
 * Read-only without `CLASS_MANAGE`; the server enforces the same split. The
 * list is tens of subjects, so it arrives whole and the tabs and search filter
 * it here, at once — the counts on the tabs are the server's, over every live
 * subject, never what is left after a search.
 */
export function SubjectsPage() {
  const permissions = useAuthStore((state) => state.user?.permissions);
  const canManage = Boolean(permissions?.includes('CLASS_MANAGE'));

  // A link from class setup lands on one class's lists, that class picked.
  const [searchParams] = useSearchParams();
  const linkedClassUuid = searchParams.get(SUBJECTS_QUERY.class) ?? undefined;
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  const mode: Mode = filters[SUBJECTS_QUERY.view] === SUBJECTS_BY_CLASS_VIEW ? 'BY_CLASS' : 'LIST';
  const { category: view, search } = filters;
  const setMode = (next: Mode) =>
    setFilters({ [SUBJECTS_QUERY.view]: next === 'BY_CLASS' ? SUBJECTS_BY_CLASS_VIEW : '' });
  const setView = (next: View) => setFilters({ category: next });
  const setSearch = (next: string) => setFilters({ search: next });
  const isSuspendedView = view === 'SUSPENDED';

  const { data, isLoading, error, refetch } = useApiQuery(
    () => subjectsApi.list({ status: isSuspendedView ? 'suspended' : 'active' }),
    [isSuspendedView],
  );

  const [editing, setEditing] = useState<Subject | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [suspending, setSuspending] = useState<Subject | null>(null);
  const [isSuspending, setIsSuspending] = useState(false);
  /** The subject being reinstated, so its menu cannot be pressed twice. */
  const [reinstatingUuid, setReinstatingUuid] = useState<string | null>(null);

  const counts = data?.counts;
  const totalLive = counts ? counts.CORE + counts.ELECTIVE + counts.LANGUAGE : undefined;

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (data?.items ?? []).filter(
      (subject) =>
        (view === 'ALL' || view === 'SUSPENDED' || subject.category === view) &&
        (!needle || subject.name.toLowerCase().includes(needle)),
    );
  }, [data, view, search]);

  const tabs: TabOption<View>[] = [
    { value: 'ALL', label: 'All subjects', badge: totalLive },
    ...SUBJECT_CATEGORY_ORDER.map((category) => ({
      value: category,
      label: SUBJECT_CATEGORY_PLURAL[category],
      badge: counts?.[category],
    })),
    { value: 'SUSPENDED', label: 'Suspended' },
  ];

  function openForm(subject: Subject | null) {
    setEditing(subject);
    setIsFormOpen(true);
  }

  function failed(title: string, caught: unknown) {
    toast.error(title, caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.');
  }

  async function confirmSuspend() {
    if (!suspending) return;
    const target = suspending;
    setIsSuspending(true);
    try {
      await subjectsApi.suspend(target.uuid);
      toast.success(
        `${capitalizeWords(target.name)} deleted`,
        target.usedBy.length > 0
          ? `Taken off ${target.usedBy.length} ${target.usedBy.length === 1 ? 'class' : 'classes'}. Its past records are kept.`
          : 'Classes can no longer pick it. Its past records are kept.',
      );
      setSuspending(null);
      refetch();
    } catch (caught) {
      // The dialog closes first: a toast under an open dialog sits behind it.
      setSuspending(null);
      failed(`Could not delete ${capitalizeWords(target.name)}`, caught);
    } finally {
      setIsSuspending(false);
    }
  }

  async function reinstate(subject: Subject) {
    setReinstatingUuid(subject.uuid);
    try {
      const saved = await subjectsApi.reinstate(subject.uuid);
      toast.success(`${capitalizeWords(saved.name)} reactivated`, `Back on the list as ${SUBJECT_CATEGORY_LABEL[saved.category].toLowerCase()}.`);
      refetch();
    } catch (caught) {
      failed(`Could not reactivate ${capitalizeWords(subject.name)}`, caught);
    } finally {
      setReinstatingUuid(null);
    }
  }


  const columns: Column<Subject>[] = [
    {
      key: 'name',
      header: 'Subject Name',
      render: (subject) => {
        const initial = subject.name.charAt(0).toUpperCase();
        const iconColor =
          subject.category === 'CORE'
            ? 'bg-blue-50 text-blue-600 dark:bg-blue-950/50 dark:text-blue-400 border-blue-200/70 dark:border-blue-900/40'
            : subject.category === 'ELECTIVE'
              ? 'bg-purple-50 text-purple-600 dark:bg-purple-950/50 dark:text-purple-400 border-purple-200/70 dark:border-purple-900/40'
              : 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400 border-emerald-200/70 dark:border-emerald-900/40';

        return (
          <div className="flex items-center gap-3 py-0.5">
            <div
              className={cn(
                'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border text-xs font-black shadow-2xs',
                iconColor,
              )}
              aria-hidden
            >
              {initial}
            </div>
            <div className="min-w-0">
              <p className="truncate font-bold text-slate-900 dark:text-white capitalize text-sm">
                {subject.name}
              </p>
              <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                {SUBJECT_CATEGORY_LABEL[subject.category]}
                {subject.isSuspended && (
                  <span className="ml-1.5 font-bold text-rose-500">· Suspended</span>
                )}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      key: 'category',
      header: 'Category',
      render: (subject) => (
        <Badge tone={SUBJECT_CATEGORY_TONE[subject.category]}>{SUBJECT_CATEGORY_LABEL[subject.category]}</Badge>
      ),
    },
    {
      key: 'usedBy',
      header: 'Taught This Year In',
      hideOnMobile: true,
      render: (subject) => {
        if (subject.usedBy.length === 0) {
          return (
            <span className="text-xs font-medium text-slate-400 dark:text-slate-500">
              {subject.isSuspended ? '—' : 'No classes assigned'}
            </span>
          );
        }
        return (
          <div className="flex flex-wrap items-center gap-1.5 py-0.5">
            {subject.usedBy.slice(0, CLASSES_SHOWN).map((cls) => (
              <span
                key={cls}
                className="inline-flex items-center rounded-lg border border-slate-200/80 bg-slate-50 px-2 py-0.5 text-xs font-semibold text-slate-700 shadow-2xs dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
              >
                {cls}
              </span>
            ))}
            {subject.usedBy.length > CLASSES_SHOWN && (
              <span
                className="inline-flex items-center rounded-lg border border-slate-200/60 bg-slate-100/70 px-1.5 py-0.5 text-[11px] font-bold text-slate-500 dark:border-white/10 dark:bg-white/10 dark:text-slate-400"
                title={subject.usedBy.slice(CLASSES_SHOWN).join(', ')}
              >
                +{subject.usedBy.length - CLASSES_SHOWN} more
              </span>
            )}
          </div>
        );
      },
    },
    ...(canManage
      ? [
          {
            key: 'actions',
            header: <span className="sr-only">Actions</span>,
            align: 'right' as const,
            render: (subject: Subject) => (
              <div className="flex items-center justify-end gap-1">
                {subject.isSuspended ? (
                  <button
                    type="button"
                    disabled={reinstatingUuid === subject.uuid}
                    onClick={() => void reinstate(subject)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-950/40 transition-colors cursor-pointer disabled:opacity-50"
                    title={`Reactivate ${capitalizeWords(subject.name)}`}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    <span>Reactivate</span>
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => openForm(subject)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-blue-600 hover:bg-blue-50 hover:text-blue-700 dark:text-blue-400 dark:hover:bg-blue-950/40 dark:hover:text-blue-300 transition-colors cursor-pointer"
                      title={`Edit ${capitalizeWords(subject.name)}`}
                    >
                      <PencilLine className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setSuspending(subject)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:text-rose-400 dark:hover:bg-rose-950/40 dark:hover:text-rose-300 transition-colors cursor-pointer"
                      title={`Delete ${capitalizeWords(subject.name)}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  const hasSearch = search.trim().length > 0;

  return (
    <>
      <PageHeader
        eyebrow="Academics"
        title="Subjects"
        description="Every subject your school teaches, as a core subject, an elective or a language. Classes pick their subjects from this list."
        actions={
          canManage ? (
            <Button className="rounded-xl bg-indigo-600 tracking-wide hover:bg-indigo-700 text-white px-4 py-2 text-sm font-bold inline-flex items-center gap-2 transition-all cursor-pointer shadow-sm shadow-indigo-500/20 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2" leftIcon={<Plus className="h-4 w-4" />}  onClick={() => openForm(null)}>
              Add subject
            </Button>
          ) : undefined
        }
      />

      {/* Highlighted View Mode Switcher ("All subjects" vs "By class") */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div
          role="tablist"
          aria-label="How to view subjects"
          className="inline-flex items-center p-1.5 rounded-2xl bg-slate-100 dark:bg-slate-800/70 border border-slate-200/80 dark:border-white/10 shadow-xs gap-1.5"
        >
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'LIST'}
            onClick={() => setMode('LIST')}
            className={cn(
              'flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-extrabold transition-all cursor-pointer',
              mode === 'LIST'
                ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/30 ring-2 ring-indigo-600/20'
                : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-white/60 dark:hover:bg-white/5',
            )}
          >
            <BookOpen className="h-4 w-4" />
            <span className="text-base font-semibold">All subjects</span>
            {totalLive !== undefined && (
              <span
                className={cn(
                  'ml-1 px-2 py-0.5 rounded-full text-xs font-black tabular-nums',
                  mode === 'LIST'
                    ? 'bg-white/20 text-white'
                    : 'bg-slate-200/80 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
                )}
              >
                {totalLive}
              </span>
            )}
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={mode === 'BY_CLASS'}
            onClick={() => setMode('BY_CLASS')}
            className={cn(
              'flex items-center gap-2 px-5 py-2.5 rounded-xl text-base font-semibold transition-all cursor-pointer',
              mode === 'BY_CLASS'
                ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/30 ring-2 ring-indigo-600/20'
                : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-white/60 dark:hover:bg-white/5',
            )}
          >
            <Layers className="h-4 w-4" />
            <span>By class</span>
          </button>
        </div>

        {mode === 'LIST' && (
          <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">
            Showing <span className="font-bold text-slate-800 dark:text-slate-200">{visible.length}</span> {visible.length === 1 ? 'subject' : 'subjects'}
          </div>
        )}
      </div>

      {mode === 'BY_CLASS' ? (
        <div className="card rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-[#1b1d34] shadow-xs overflow-hidden">
          <ClassSubjectsView
            canManage={canManage}
            initialClassUuid={linkedClassUuid}
            onChanged={refetch}
          />
        </div>
      ) : (
        <div className="card rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-[#1b1d34] shadow-xs overflow-hidden">
          {/* Category Tabs & Search Bar */}
          <div className="flex flex-col gap-3 border-b border-slate-200/80 dark:border-white/10 p-3.5 sm:p-4 bg-slate-50/50 dark:bg-white/[0.02]">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <Tabs label="Subject categories" options={tabs} value={view} onChange={setView} />
              <div className="w-full sm:w-72 relative">
                <Input
                  label="Search subjects"
                  hideLabel
                  placeholder="Search subjects..."
                  autoComplete="off"
                  leftIcon={<Search className="h-4 w-4 text-slate-400" />}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer"
                    title="Clear search"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
            {hasSearch && (
              <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 px-0.5">
                <span>
                  Found <strong className="font-bold text-slate-700 dark:text-slate-200">{visible.length}</strong> matching "{search}"
                </span>
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="text-xs font-semibold text-indigo-600 hover:underline dark:text-indigo-400 cursor-pointer"
                >
                  Clear search
                </button>
              </div>
            )}
          </div>

          {error ? (
            <div className="p-4">
              <QueryError title="Could not load the subjects" error={error} onRetry={refetch} />
            </div>
          ) : (
            <Table
              variant="plain"
              columns={columns}
              rows={visible}
              rowKey={(subject) => subject.uuid}
              isLoading={isLoading}
              empty={
                <EmptyState
                  icon={<BookOpen className="h-5 w-5" />}
                  title={
                    hasSearch
                      ? 'No subject matches that search'
                      : isSuspendedView
                        ? 'No suspended subjects'
                        : view === 'ALL'
                          ? 'No subjects yet'
                          : `No ${SUBJECT_CATEGORY_PLURAL[view].toLowerCase()} yet`
                  }
                  description={
                    hasSearch
                      ? 'Check the spelling, or clear the search to see the whole list.'
                      : isSuspendedView
                        ? 'A subject you suspend is kept here, and can be reactivated.'
                        : canManage
                          ? 'Add the subjects your school teaches. Classes then pick theirs from this list.'
                          : 'Your school office has not added any subjects yet.'
                  }
                  action={
                    canManage && !hasSearch && !isSuspendedView ? (
                      <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => openForm(null)}>
                        Add subject
                      </Button>
                    ) : undefined
                  }
                />
              }
            />
          )}
        </div>
      )}

      <SubjectFormModal
        subject={editing}
        defaultCategory={view === 'ALL' || view === 'SUSPENDED' ? undefined : view}
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSaved={(saved) => {
          setIsFormOpen(false);
          toast.success(editing ? `${capitalizeWords(saved.name)} saved` : `${capitalizeWords(saved.name)} added`, SUBJECT_CATEGORY_PLURAL[saved.category]);
          refetch();
        }}
      />

      <ConfirmDialog
        isOpen={Boolean(suspending)}
        tone="danger"
        title={`Delete ${suspending?.name ? capitalizeWords(suspending.name) : 'this subject'}?`}
        description={
          suspending && suspending.usedBy.length > 0
            ? `It will be removed from every class in your school that teaches it this year — ${suspending.usedBy.join(', ')} — and no class can pick it again. Past homework, exams and timetables are kept. Reactivating it later puts it back on the list, not back on these classes.`
            : 'No class teaches it this year. It comes off your subject list so no class can pick it, and its past homework, exams and timetables are kept. You can reactivate it from the Suspended tab.'
        }
        confirmLabel="Delete"
        isLoading={isSuspending}
        onConfirm={() => void confirmSuspend()}
        onCancel={() => setSuspending(null)}
      />
    </>
  );
}
